// SGL-346: the chat-service routes around a card scan.
//
// The browser orchestrates and nothing proxies. Extraction and the CRM save both
// stay browser -> API Gateway -> Lambda (receptionistCardService.ts, unchanged).
// What lives here is only what a Lambda cannot give us: drafting, which needs the
// same agent prompt, brand block, model and credit gate that /chat/stream
// assembles — and has to leave API Gateway anyway, since REST cuts every request
// at 29s however long the function is allowed — plus the two calls that write to
// the conversation, which only this side owns.
import { authedFetch } from "../../auth/authedFetch";
import { CHAT_API_BASE_URL } from "../../services/chatService";
import type { Contact } from "./types/cardToCrm.types";
import type { CardContactsAttachment, ScannedCardContact } from
  "../../components/AIAgents/ChatWithAgent/types";

const base = () => (CHAT_API_BASE_URL || "").replace(/\/$/, "");

/** Server rejections carry a reason worth showing; axios-style opaque text does not. */
const explain = async (resp: Response, fallback: string): Promise<string> => {
  try {
    const data = await resp.json();
    const detail = data?.detail ?? data?.error ?? data?.message;
    if (typeof detail === "string" && detail.trim()) return detail;
    if (detail && typeof detail === "object") {
      const inner = detail.error ?? detail.message;
      if (typeof inner === "string" && inner.trim()) return inner;
    }
  } catch {
    /* no JSON body — fall through */
  }
  return fallback;
};

// `issues` is the review card's own state, recomputed by the checks — never part
// of what the conversation stores or the drafts are written from.
const stripId = ({ id, issues, ...rest }: Contact) => rest;

const toAttachment = (data: any): CardContactsAttachment => ({
  contacts: (data?.contacts ?? []).map((c: any): ScannedCardContact => {
    const d = c?.draft;
    return {
      name: c.name ?? null,
      title: c.title ?? null,
      company: c.company ?? null,
      email: c.email ?? null,
      phone: c.phone ?? null,
      mobile: c.mobile ?? null,
      website: c.website ?? null,
      // Both halves or nothing: a subject with no body renders as an approvable
      // email that is missing its content.
      draft:
        d && typeof d.subject === "string" && typeof d.body === "string"
          ? { subject: d.subject, body: d.body }
          : null,
      // Never inferred from the presence of an email — the server decides.
      sendable: c.sendable === true,
      reason: typeof c.reason === "string" && c.reason ? c.reason : null,
      saved: c.saved === true,
      sent: c.sent === true,
    };
  }),
  captureContext: typeof data?.capture_context === "string" ? data.capture_context : "",
  tags: Array.isArray(data?.tags) ? data.tags.filter((t: any) => typeof t === "string" && t.trim()) : [],
  proposedAction:
    data?.proposed_action === "save_only" || data?.proposed_action === "draft_only"
      ? data.proposed_action
      : "save_and_send",
  // Only ever true on a card restored after the owner approved. Defaulting to false
  // is the safe side of the wrong answer: it offers a send that the CRM dedupes,
  // rather than hiding one that never happened.
  saved: data?.saved === true,
  sent: data?.sent === true,
  threadId: typeof data?.thread_id === "string" ? data.thread_id : null,
  cardMessageSk: typeof data?.card_message_sk === "string" ? data.card_message_sk : null,
});

export interface RecordedScan {
  /** The thread the scan was written to — the caller adopts it so the follow-up chat
   *  turn continues this conversation instead of opening a second one. */
  threadId: string | null;
  /** The row the scan went into. Passing it back on a later call rewrites that row. */
  cardMessageSk: string | null;
}

/** POST /receptionist/card/scanned — puts the just-scanned cards into the
 *  conversation before any drafting, so the agent can answer questions about the
 *  card the owner is looking at. Writes no CRM record, sends nothing, calls no
 *  model.
 *
 *  Called again with `cardMessageSk` each time the owner corrects a field: the
 *  review card's edits used to live only in its own state until "Write the intro"
 *  was pressed, so correcting a name and then asking the agent something in the
 *  composer left it working from the raw scan (live, 2026-09-25). */
export const recordScannedCards = async (params: {
  customerId: string;
  agentId: string;
  contacts: Contact[];
  captureContext?: string;
  threadId?: string;
  /** Omit on the first call for a scan; pass it on every correction after. */
  cardMessageSk?: string | null;
}): Promise<RecordedScan> => {
  const resp = await authedFetch(`${base()}/receptionist/card/scanned`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      customer_id: params.customerId,
      agent_id: params.agentId,
      contacts: params.contacts.map(stripId),
      capture_context: params.captureContext || "",
      thread_id: params.threadId || null,
      card_message_sk: params.cardMessageSk || null,
    }),
  });
  if (!resp.ok) {
    throw new Error(await explain(resp, "Couldn't record the scan."));
  }
  const data = await resp.json();
  return {
    threadId: typeof data?.thread_id === "string" ? data.thread_id : null,
    cardMessageSk:
      typeof data?.card_message_sk === "string" ? data.card_message_sk : null,
  };
};

export interface DraftScannedCardsResult {
  attachment: CardContactsAttachment;
  /** The thread the scan was written to — the caller adopts it so the follow-up
   *  chat turn continues this conversation instead of opening a second one that
   *  knows nothing about the cards. */
  threadId: string | null;
}

/** POST /receptionist/card/draft — writes no CRM record and sends no email. It
 *  does write the drafted cards into the conversation thread, opening one when the
 *  scan is the first thing to happen. */
export const draftScannedCards = async (params: {
  customerId: string;
  agentId: string;
  contacts: Contact[];
  captureContext?: string;
  brief?: string;
  tags?: string[];
  /** Without it the scan is invisible to the next chat turn — the chat service
   *  rebuilds the model's context from the stored thread, not from what the
   *  browser is holding. */
  threadId?: string;
}): Promise<DraftScannedCardsResult> => {
  const resp = await authedFetch(`${base()}/receptionist/card/draft`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      customer_id: params.customerId,
      agent_id: params.agentId,
      contacts: params.contacts.map(stripId),
      capture_context: params.captureContext || "",
      brief: params.brief || "",
      tags: params.tags || [],
      thread_id: params.threadId || null,
    }),
  });
  if (!resp.ok) {
    throw new Error(
      await explain(resp, "Couldn't write the intro emails. Try again in a moment.")
    );
  }
  const data = await resp.json();
  return {
    attachment: toAttachment(data),
    threadId: typeof data?.thread_id === "string" ? data.thread_id : null,
  };
};

export interface CardSaveRow {
  index: number;
  ok: boolean;
  sent: boolean | null;
  send_error: string | null;
  error: string | null;
}

/** The save handler reports failures as codes. They are rendered on the card as the
 *  owner's only explanation of what went wrong, and "ghl_status_400" tells them
 *  nothing about whether to retry or to go find the person's phone number. */
const SAVE_ERRORS: Record<string, string> = {
  ghl_status_400: "GoHighLevel wouldn't accept this one — it needs an email address or a phone number.",
  ghl_status_401: "GoHighLevel needs reconnecting before this can be saved.",
  ghl_status_403: "GoHighLevel needs reconnecting before this can be saved.",
  not_connected: "GoHighLevel isn't connected yet.",
  failed: "Couldn't be saved — try again in a moment.",
  // SGL-586: refused by the save Lambda before GHL — the same checks the review
  // card flags, enforced server-side for any card that skipped that step.
  name_missing: "Not saved — no name was read from this card. Add one and save again.",
  email_domain_not_found: "Not saved — this email domain does not exist. Check the card.",
  email_domain_no_mail: "Not saved — this email domain doesn't accept email. Check the card.",
  email_invalid: "Not saved — the email address is incomplete. Check the card.",
};

export const explainSaveError = (code: unknown): string => {
  if (typeof code !== "string" || !code.trim()) return SAVE_ERRORS.failed;
  if (SAVE_ERRORS[code]) return SAVE_ERRORS[code];
  // An unmapped 5xx is ours, not the owner's fault — say so rather than showing the
  // code, but keep unknown non-status reasons, which are usually already prose.
  if (/^ghl_status_5\d\d$/.test(code)) return "GoHighLevel had a problem — try again in a moment.";
  return code;
};

/** POST /receptionist/card/approved — settle the stored turn AFTER the browser has
 *  saved to the CRM itself. Writes no contact and sends no email.
 *
 *  This used to be a /card/save route here that proxied the save-all Lambda, which
 *  made the agent call the Lambda — the shape the architecture forbids. The browser
 *  now makes both calls: `saveAllCards` to API Gateway, then this one, because the
 *  conversation row only exists on the chat service.
 *
 *  Best-effort by design: the contacts are in the CRM either way, so a failure here
 *  must never read to the owner as a failed save. Its only cost is that a reload
 *  re-offers the send. */
export const markCardsApproved = async (params: {
  customerId: string;
  threadId: string;
  cardMessageSk: string;
  contacts: (ScannedCardContact & { saved: boolean; sent: boolean; reason?: string | null })[];
}): Promise<boolean> => {
  try {
    const resp = await authedFetch(`${base()}/receptionist/card/approved`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        customer_id: params.customerId,
        thread_id: params.threadId,
        card_message_sk: params.cardMessageSk,
        contacts: params.contacts.map((c) => ({
          name: c.name,
          title: c.title,
          company: c.company,
          email: c.email,
          phone: c.phone,
          mobile: c.mobile,
          website: c.website,
          // The reviewed text, so a restored card shows what actually went out.
          draft: c.draft,
          sendable: c.sendable,
          saved: c.saved,
          sent: c.sent,
          reason: c.reason ?? null,
        })),
      }),
    });
    return resp.ok;
  } catch {
    return false;
  }
};
